/** Reporting-day key shift. Day keys are calendar dates, so UTC date math does not depend on TZ. */
export function shiftReportingDayKey(day: string, deltaDays: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day.trim());
  if (!m) return "";
  const dt = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(dt.getTime())) return "";
  dt.setUTCDate(dt.getUTCDate() + deltaDays);
  return dt.toISOString().slice(0, 10);
}

/** Monday (`YYYY-MM-DD`) of the reporting week that contains `day`. Sunday belongs to the previous Monday. */
export function reportingWeekMonday(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day.trim());
  if (!m) return "";
  const dt = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(dt.getTime())) return "";
  const dow = dt.getUTCDay();
  const delta = dow === 0 ? -6 : 1 - dow;
  return shiftReportingDayKey(day, delta);
}

export interface WeekableUptimeDay {
  day: string;
  uptimeHours: number;
  scheduledHours: number;
  capacityHours: number;
  unmeasuredHours: number;
  expectedHours: number;
}

export interface UptimeWeekBucket {
  monday: string;
  sunday: string;
  uptimeHours: number;
  scheduledHours: number;
  capacityHours: number;
  unmeasuredHours: number;
  expectedHours: number;
}

/**
 * A reporting day has finished when fleet-additive capacity is a full DST day per table
 * (23h spring-forward). Today is clipped to elapsed hours; future days are 0.
 */
export function isCompleteUptimeDay(capacityHours: number, tableCount: number): boolean {
  if (!(tableCount > 0) || !(capacityHours > 0)) return false;
  return capacityHours >= tableCount * 22;
}

/**
 * Complete Monday–Sunday weeks whose seven days are all in `rows`.
 * Incomplete edge weeks (range starts mid-week, current week still in progress, future days) are omitted.
 */
export function groupFullMondayWeeks(
  rows: readonly WeekableUptimeDay[],
  tableCount: number,
): UptimeWeekBucket[] {
  if (tableCount <= 0 || rows.length === 0) return [];
  const byDay = new Map(rows.map((row) => [row.day, row]));
  const mondays = new Set<string>();
  for (const row of rows) {
    const monday = reportingWeekMonday(row.day);
    if (monday) mondays.add(monday);
  }
  const weeks: UptimeWeekBucket[] = [];
  for (const monday of [...mondays].sort()) {
    const days = Array.from({ length: 7 }, (_, i) => shiftReportingDayKey(monday, i));
    if (days.some((day) => !day || !byDay.has(day))) continue;
    const dayRows = days.map((day) => byDay.get(day)!);
    if (dayRows.some((row) => !isCompleteUptimeDay(row.capacityHours, tableCount))) continue;
    weeks.push({
      monday,
      sunday: days[6]!,
      uptimeHours: dayRows.reduce((sum, row) => sum + row.uptimeHours, 0),
      scheduledHours: dayRows.reduce((sum, row) => sum + row.scheduledHours, 0),
      capacityHours: dayRows.reduce((sum, row) => sum + row.capacityHours, 0),
      unmeasuredHours: dayRows.reduce((sum, row) => sum + row.unmeasuredHours, 0),
      expectedHours: dayRows.reduce((sum, row) => sum + row.expectedHours, 0),
    });
  }
  return weeks;
}

export const FULL_WEEKS_EMPTY_MESSAGE =
  "No complete Monday–Sunday week in this range. Change to a range covering a whole Monday to Sunday period, or untick the Full weeks toggle.";

/**
 * Sum of measured uptime on the previous Monday–Sunday.
 * Undefined when any of those seven days is missing from `measuredUptimeByDay`
 * (unmeasured days are omitted from that map, matching the daily prior-week line).
 */
export function priorWeekMeasuredUptime(
  monday: string,
  measuredUptimeByDay: ReadonlyMap<string, number>,
): number | undefined {
  const priorMonday = shiftReportingDayKey(monday, -7);
  if (!priorMonday) return undefined;
  let sum = 0;
  for (let i = 0; i < 7; i++) {
    const day = shiftReportingDayKey(priorMonday, i);
    const hours = day ? measuredUptimeByDay.get(day) : undefined;
    if (hours == null) return undefined;
    sum += hours;
  }
  return sum;
}

function dayMonth(day: string): { day: number; month: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day.trim());
  if (!m) return null;
  const month = Number(m[2]);
  const date = Number(m[3]);
  if (month < 1 || month > 12 || date < 1 || date > 31) return null;
  return { day: date, month };
}

/** Compact week axis label. Same month: `18–24/8`. Cross-month: `31/8–6/9`. */
export function formatWeekAxisLabel(monday: string, sunday: string): string {
  const start = dayMonth(monday);
  const end = dayMonth(sunday);
  if (!start || !end) return monday;
  if (start.month === end.month) return `${start.day}–${end.day}/${start.month}`;
  return `${start.day}/${start.month}–${end.day}/${end.month}`;
}
